<template>
  <!-- prettier-ignore -->
  <div :class="classes">
    <div class="input-elements">
      <Form.Dropdown
        ref="dialCode"
        class="input__dialcode"
        name="phone_code"
        :model-value="formData.dialCode"
        :options="countries"
        :required="isRequired"
        :theme="theme"
        :title="tm('person.phonePrefix')"
        :readonly="pending"
        @update:model-value="setDialCode"
      />

      <Form.Input
        ref="number"
        autocomplete="tel"
        class="input__number"
        name="phone_number"
        :model-value="formData.number"
        :autofocus="props.autofocus"
        :placeholder="placeholder"
        :readonly="pending"
        :required="isRequired"
        :theme="theme"
        :title="title"
        :type="browser.isDesktop ? 'text' : 'tel'"
        :cy-test="formData.dialCode + formData.number"
        @blur="event => setNumber(event.target.value.trim(), true)"
        @update="input => setNumber(input)"
      ><slot /></Form.Input>

      <div
        v-show="isPending"
        class="input__loader"><span>⚆</span></div>
    </div>
    <div class="input__hint">{{ autoHint }}</div>
  </div>
</template>

<script setup>
import { computed, nextTick, reactive, watchEffect } from 'vue'

import debounce from 'lodash/debounce'

import { getCountries } from '@/config/enums'

import { DEFAULT_DELAY_TIME } from '@/config/constants'
import * as Form from '@/components/Form'
import { useI18n } from 'vue-i18n'
import { useBrowser } from '@/hooks'

const $emit = defineEmits(['update:modelValue'])

// hooks
const { locale, tm, t } = useI18n()
const { browser } = useBrowser()
// props
const props = defineProps({
  autofocus: {
    type: Boolean,
    default: false,
  },

  defaultDialCode: {
    type: String,
    default: '+41',
  },

  delay: {
    type: Number,
    default: DEFAULT_DELAY_TIME * 1.5,
  },

  errors: {
    type: Object,
    default: () => ({}),
  },

  placeholder: {
    type: String,
    default: '',
  },

  readonly: {
    type: Boolean,
    default: false,
  },

  required: {
    type: Boolean,
    default: false,
  },

  theme: {
    type: String,
    default: 'light',
  },

  title: {
    type: String,
    default: '',
  },

  validate: {
    type: Object,
    default: () => {},
  },

  modelValue: {
    type: String,
    default: '',
  },
})

// data
const formData = reactive({
  dialCode: props.defaultDialCode,
  number: null,
})

// computed
const autoHint = computed(() => {
  let hints = []
  const errors = Array.from(props.validate?.$errors)

  let number = null
  switch (formData.dialCode) {
    case '+41':
      number = '+41 771002030'
      break

    case '+49':
      number = '+49 15210020030'
      break

    case '+39':
      number = '+39 3231002003'

      break
  }

  if (number) {
    hints.push(t('form.phoneHint', { number }))
  }

  if (invalid.value && errors.length > 0) {
    const error = errors[0]?.$validator
    hints.push(tm(`form.error.${error}`))
  }
  return hints.join('; ')
})

const classes = computed(() => {
  return {
    input: true,
    'input--invalid': invalid.value,
    [`input--${props.theme}`]: true,
  }
})

const countries = computed(() => {
  return getCountries(locale.value).map(country => {
    return {
      code: country.code,
      value: country.dialCode,
      text: `${country.text} (${country.dialCode})`,
      display: country.dialCode,
    }
  })
})

const invalid = computed(() => {
  return props.validate?.$invalid && props.validate?.$error
})

const isRequired = computed(() => {
  return props.required || props.validate?.required !== undefined
})

const isPending = computed(() => {
  return props.validate?.$pending || false
})

const pending = computed(() => {
  return (props.validate?.$pending || props.readonly) ?? false
})
// methods
const update = debounce(emitUpdate, props.delay)

function emitUpdate() {
  const { dialCode, number } = formData

  const newValue = `${dialCode}${number}`
  if (dialCode && number && props.modelValue !== newValue) {
    props.validate?.$touch()
    $emit('update:modelValue', newValue)
  }
}

function getDialCode(input) {
  const country = countries.value.find(currentCountry => input.match(`^\\${currentCountry.value}`))
  if (country) {
    return country.value
  }
}

function setDialCode(input) {
  formData.dialCode = input
  update()
}

function setNumber(input, forceUpdate = false) {
  let number = input

  if (input[0] === '+') {
    const dialCode = getDialCode(input)
    if (dialCode) {
      number = number.substring(dialCode.length)
      setDialCode(dialCode)
    }
  }

  formData.number = number

  if (forceUpdate) {
    emitUpdate()
  } else {
    update()
  }
}

// watchers
watchEffect(() => {
  const value = props.modelValue
  if (value) {
    nextTick(() => {
      setNumber(value)
    })
  }
})
</script>

<style name="mobile" scoped>
.input {
  &-elements {
    display: flex;
    flex-direction: row;
    position: relative;

    &:after {
      content: '';
      position: absolute;
      height: 1px;
      width: 100%;
      bottom: 4px;
      left: 0;

      background-color: var(--c-black);
    }
  }

  &__loader {
    position: absolute;
    top: 0;
    right: 0;
    height: 44px;
    width: 44px;
    font-size: 20px;
    line-height: 44px;
    text-align: center;
    cursor: wait;
    background-color: var(--c-white);

    span {
      display: inline-block;
      animation: spin 2s linear infinite;
    }
  }

  &--dark {
    .input__loader {
      background-color: var(--c-warm-gray);
    }
  }

  &__dialcode {
    flex: 0 1 85px;
    display: inline-block;
  }

  &__number {
    flex: 1 1 auto;
  }

  &__dialcode,
  &__number {
    & :deep(.input-element) {
      border-bottom: 0;
    }
  }

  &__hint {
    font-size: 15px;
    line-height: 20px;
    color: #999999;
    padding: 0 0 0 10px;
    margin-top: -5px;

    transition: color 0.2s ease-in-out;
  }

  &--invalid {
    .input__hint,
    & :deep(.input__title) {
      color: var(--c-error);
    }

    .input-elements {
      &:after {
        background-color: var(--c-error);
      }
    }
  }

  @keyframes spin {
    100% {
      -webkit-transform: rotate(360deg);
      transform: rotate(360deg);
    }
  }
}
</style>

<style name="safari" scoped>
@media not all and (min-resolution: 0.001dpcm) {
  @media {
    .input {
      &__hint {
        margin-top: 0;
      }

      .input__number :deep(.input-element) {
        height: 45px;
      }
    }
  }
}
</style>
